import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import {
  transformToInsuredMembers,
  transformToInsuredMembersForPutQuote,
} from "../../utils/gigHelpers";
import { getQuote, getTakafulMasterData } from "../../features/product";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../common/loader";
import { getExcelDate } from "../../utils/commonHelper";
import {
  nationality as nationalityMasterData,
  relation as relationShipMasterData,
} from "../../config/oqic";
import { extractSelectedSchemes } from "../../utils/oqicHelpers";
import Stepper from "../Stepper";
import SelectDropdown from "../SelectDropdown";

const ageCategories = [
  { key: "total_adults_(age_18_or_above)", labelKey: "oquic.personDetails.ageGroups.adults" },
  { key: "total_childs_(age_18_below)", labelKey: "oquic.personDetails.ageGroups.children" },
];

const getAgeGroupRanges = () => {
  const today = new Date();
  const getDateYearsAgo = (years) => {
    const date = new Date(today);
    date.setFullYear(date.getFullYear() - years);
    return date.toISOString().split("T")[0];
  };

  return {
    "total_adults_(age_18_or_above)": {
      min: getDateYearsAgo(120),
      max: getDateYearsAgo(18),
    },
    "total_childs_(age_18_below)": {
      min: getDateYearsAgo(18),
      max: getDateYearsAgo(0),
    },
  };
};

const OquicPersonDetailsForm = () => {
  const { t } = useTranslation(); // ✅ Add translation hook
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { quoteLoader } = useSelector((state) => state.product);

  const prefillCounts = useMemo(() => {
    return location?.state?.prefillCounts || {};
  }, [location?.state]);

  const planData = useMemo(
    () => location?.state?.data || {},
    [location?.state]
  );

  const [formData, setFormData] = useState({});
  const [people, setPeople] = useState([]);
  const [errors, setErrors] = useState({});

  const handleCountChange = (e, key) => {
    const value = parseInt(e.target.value) || 0;
    const newFormData = { ...formData, [key]: value };
    setFormData(newFormData);

    const ranges = getAgeGroupRanges();
    const newPeople = [];

    Object.entries(newFormData).forEach(([categoryKey, count]) => {
      for (let i = 0; i < count; i++) {
        newPeople.push({
          person: {
            srNo: i + 1,
            name: "",
            passportNo: "",
            dob: "",
            gender: "",
            relationShip: "",
            nationality: "",
            winSportsStr:
              categoryKey === "total_childs_(age_18_below)" ? "0" : "1",
          },
          groupKey: categoryKey,
          dateLimits: ranges[categoryKey],
        });
      }
    });

    setPeople(newPeople);
  };

  const handlePersonChange = (index, field, value) => {
    const updatedPeople = [...people];
    updatedPeople[index].person[field] = value;

    if (field === "governate") {
      dispatch(getTakafulMasterData(`wilayat?governorate=${value}`));
    }
    setPeople(updatedPeople);

    // Clear error for this field
    const fieldKey = `person_${index}`;
    if (errors[fieldKey]?.[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        if (updated[fieldKey]) {
          const { [field]: _, ...rest } = updated[fieldKey];
          updated[fieldKey] = rest;
          if (Object.keys(rest).length === 0) delete updated[fieldKey];
        }
        return updated;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (people.length === 0) {
      toast.error(t("oquic.personDetails.validationMessages.addPeople") || "Please add at least one person");
      return false;
    }

    people.forEach((personObj, index) => {
      const personErrors = {};
      const fieldKey = `person_${index}`;

      if (!personObj.person.name || personObj.person.name.trim() === "") {
        personErrors.name = t("oquic.personDetails.validationMessages.nameRequired");
      }

      if (!personObj.person.passportNo || personObj.person.passportNo.trim() === "") {
        personErrors.passportNo = t("oquic.personDetails.validationMessages.passportRequired");
      }

      if (!personObj.person.dob || personObj.person.dob.trim() === "") {
        personErrors.dob = t("oquic.personDetails.validationMessages.dobRequired");
      }

      if (!personObj.person.gender || personObj.person.gender === "") {
        personErrors.gender = t("oquic.personDetails.validationMessages.genderRequired");
      }

      if (!personObj.person.relationShip || personObj.person.relationShip === "") {
        personErrors.relationShip = t("oquic.personDetails.validationMessages.relationshipRequired");
      }

      if (!personObj.person.nationality || personObj.person.nationality === "") {
        personErrors.nationality = t("oquic.personDetails.validationMessages.nationalityRequired");
      }

      // First person additional fields
      if (index === 0) {
        if (!personObj.person.email || personObj.person.email.trim() === "") {
          personErrors.email = t("oquic.personDetails.validationMessages.emailRequired");
        }
        if (!personObj.person.mobile_no || personObj.person.mobile_no.trim() === "") {
          personErrors.mobile_no = t("oquic.personDetails.validationMessages.mobileRequired");
        }
        if (!personObj.person.address || personObj.person.address.trim() === "") {
          personErrors.address = t("oquic.personDetails.validationMessages.addressRequired");
        }
        if (!personObj.person.city || personObj.person.city.trim() === "") {
          personErrors.city = t("oquic.personDetails.validationMessages.cityRequired");
        }
        if (!personObj.person.poBox || personObj.person.poBox.trim() === "") {
          personErrors.poBox = t("oquic.personDetails.validationMessages.poBoxRequired");
        }
      }

      if (Object.keys(personErrors).length > 0) {
        newErrors[fieldKey] = personErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    let adults = people
      .filter((item) => item.groupKey === "total_adults_(age_18_or_above)")
      .map((item) => ({
        ...item.person,
        dob:
          item.person.dob.substring(8) +
          "/" +
          item.person.dob.substring(5, 7) +
          "/" +
          item.person.dob.substring(0, 4),
      }));
    let childrens = people
      .filter((item) => item.groupKey === "total_childs_(age_18_below)")
      .map((item) => ({
        ...item.person,
        dob:
          item.person.dob.substring(8) +
          "/" +
          item.person.dob.substring(5, 7) +
          "/" +
          item.person.dob.substring(0, 4),
      }));

    const { extracted_data } = planData;

    const reqData = {
      planData,
      ...extracted_data,
      insurance_company_product_mapping_id:
        planData?.insurance_company_product_mapping_id,
      company_id: planData?.company_id,
      schemes: extractSelectedSchemes(planData),
      address: adults[0]?.address,
      insuredName: adults[0]?.name,
      poBox: adults[0]?.poBox,
      city: adults[0]?.city,
      children: childrens,
      adults: adults,
    };

    dispatch(getQuote(reqData)).then((res) => {
      navigate("/checkout", { state: { existingData: res.payload } });
    });
  };

  useEffect(() => {
    if (Object.keys(prefillCounts).length > 0) {
      setFormData(prefillCounts);

      const ranges = getAgeGroupRanges();
      const initializedPeople = [];

      const contactData = {
        email: planData?.email || "",
        mobile_no: planData?.mobile_no || "",
        address: "",
        city: "",
        poBox: "",
      };

      Object.entries(prefillCounts).forEach(([categoryKey, count]) => {
        for (let i = 0; i < count; i++) {
          initializedPeople.push({
            person:
              i == 0
                ? {
                    srNo: i + 1,
                    name: "",
                    passportNo: "",
                    dob: "",
                    gender: "",
                    relationShip: "",
                    nationality: "",
                    winSportsStr:
                      categoryKey === "total_childs_(age_18_below)" ? "0" : "1",
                    ...contactData,
                  }
                : {
                    srNo: i + 1,
                    name: "",
                    passportNo: "",
                    dob: "",
                    gender: "",
                    relationShip: "",
                    nationality: "",
                    winSportsStr:
                      categoryKey === "total_childs_(age_18_below)" ? "0" : "1",
                  },
            groupKey: categoryKey,
            dateLimits: ranges[categoryKey],
          });
        }
      });

      setPeople(initializedPeople);
    }
  }, [prefillCounts]);

  if (quoteLoader) {
    return <Loader type="logo-gif" />;
  }

  return (
    <div className="container py-4">
      <div className="policy-formBox why-us-form">
        <form onSubmit={handleSubmit}>
          <h3 className="pb-3 fw-bold text-center">
            {t("oquic.personDetails.title")} {/* ✅ Translated heading */}
          </h3>

          <div className="row">
            {ageCategories.map(({ key, labelKey }) => (
              <div className="col-md-6 col-sm-12" key={key}>
                <div className="form-group">
                  <label className="form-label">
                    {t(labelKey)} {/* ✅ Translated age group label */}
                  </label>
                  <input
                    type="number"
                    className="form-control element-style"
                    min="0"
                    value={formData[key] || ""}
                    onChange={(e) => handleCountChange(e, key)}
                  />
                </div>
              </div>
            ))}
          </div>

          {people.length > 0 && <hr className="my-4" />}

          {people.map((personObj, index) => {
            const ageCategoryLabel =
              ageCategories.find((a) => a.key === personObj.groupKey)?.labelKey ||
              "";
            const personErrors = errors[`person_${index}`] || {};

            return (
              <div className="card mb-4" key={"people_form_card_" + index}>
                <div className="card-header">
                  <strong>
                    {t("oquic.personDetails.personCard.title")} {index + 1}{" "}
                    {t("oquic.personDetails.personCard.separator")}{" "}
                    {ageCategoryLabel && t(ageCategoryLabel)}
                  </strong>
                </div>
                <div className="card-body row gx-3 gy-1">
                  {/* Name */}
                  <div className="col-md-6 col-sm-12">
                    <div className="form-group">
                      <label className="form-label">
                        {t("oquic.personDetails.formLabels.name")}
                      </label>
                      <input
                        type="text"
                        className={`form-control element-style ${personErrors.name ? "is-invalid" : ""}`}
                        value={personObj.person.name}
                        onChange={(e) =>
                          handlePersonChange(index, "name", e.target.value)
                        }
                      />
                      {personErrors.name && <div className="invalid-feedback d-block">{personErrors.name}</div>}
                    </div>
                  </div>

                  {/* Passport Number */}
                  <div className="col-md-6 col-sm-12">
                    <div className="form-group">
                      <label className="form-label">
                        {t("oquic.personDetails.formLabels.passportNumber")}
                      </label>
                      <input
                        type="text"
                        className={`form-control element-style ${personErrors.passportNo ? "is-invalid" : ""}`}
                        value={personObj.person.passportNo}
                        onChange={(e) =>
                          handlePersonChange(index, "passportNo", e.target.value)
                        }
                      />
                      {personErrors.passportNo && <div className="invalid-feedback d-block">{personErrors.passportNo}</div>}
                    </div>
                  </div>

                  {/* Birth Date */}
                  <div className="col-md-6 col-sm-12">
                    <label className="form-label">
                      {t("oquic.personDetails.formLabels.birthDate")}
                    </label>
                    <input
                      type="date"
                      className={`form-control element-style ${personErrors.dob ? "is-invalid" : ""}`}
                      name="insured_dob"
                      min={personObj.dateLimits.min}
                      max={personObj.dateLimits.max}
                      value={personObj.person.dob}
                      onChange={(e) =>
                        handlePersonChange(index, "dob", e.target.value)
                      }
                    />
                    <small className="text-muted">
                      {t("oquic.personDetails.dateRange.allowed")}{" "}
                      {personObj.dateLimits.min} {t("oquic.personDetails.dateRange.to")}{" "}
                      {personObj.dateLimits.max}
                    </small>
                    {personErrors.dob && <div className="invalid-feedback d-block">{personErrors.dob}</div>}
                  </div>

                  {/* Gender */}
                  <div className="col-md-6 col-sm-12">
                    <div className="form-group">
                      <label className="form-label">
                        {t("oquic.personDetails.formLabels.gender")}
                      </label>
                      <SelectDropdown
                        name="gender"
                        placeholder={t("oquic.personDetails.formLabels.selectGender")}
                        options={[
                          { label: t("oquic.personDetails.formLabels.male"), value: "1" },
                          { label: t("oquic.personDetails.formLabels.female"), value: "2" },
                        ]}
                        defaultValue={personObj.person.gender ? { value: personObj.person.gender } : null}
                        handleOnChange={(name, value) =>
                          handlePersonChange(index, "gender", value)
                        }
                        isSearchable={false}
                      />
                      {personErrors.gender && <div className="invalid-feedback d-block">{personErrors.gender}</div>}
                    </div>
                  </div>

                  {/* Relationship */}
                  <div className="col-md-6 col-sm-12">
                    <div className="form-group">
                      <label className="form-label">
                        {t("oquic.personDetails.formLabels.relationship")}
                      </label>
                      <SelectDropdown
                        name="relationShip"
                        placeholder={t("oquic.personDetails.formLabels.selectRelationship")}
                        options={relationShipMasterData?.map((item) => ({
                          label: item.description,
                          value: item.relation_code,
                        })) || []}
                        defaultValue={personObj.person.relationShip ? { value: personObj.person.relationShip } : null}
                        handleOnChange={(name, value) =>
                          handlePersonChange(index, "relationShip", value)
                        }
                        isSearchable={false}
                      />
                      {personErrors.relationShip && <div className="invalid-feedback d-block">{personErrors.relationShip}</div>}
                    </div>
                  </div>

                  {/* Nationality */}
                  <div className="col-md-6 col-sm-12">
                    <div className="form-group">
                      <label className="form-label">
                        {t("oquic.personDetails.formLabels.nationality")}
                      </label>
                      <SelectDropdown
                        name="nationality"
                        placeholder={t("oquic.personDetails.formLabels.selectNationality")}
                        options={nationalityMasterData?.map((item) => ({
                          label: item.nationality_description,
                          value: item.nationality_codes,
                        })) || []}
                        defaultValue={personObj.person.nationality ? { value: personObj.person.nationality } : null}
                        handleOnChange={(name, value) =>
                          handlePersonChange(index, "nationality", value)
                        }
                      />
                      {personErrors.nationality && <div className="invalid-feedback d-block">{personErrors.nationality}</div>}
                    </div>
                  </div>

                  {/* First person only fields */}
                  {index === 0 && (
                    <>
                      {/* Email */}
                      <div className="col-md-6 col-sm-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("oquic.personDetails.formLabels.email")}
                          </label>
                          <input
                            type="email"
                            className={`form-control element-style ${personErrors.email ? "is-invalid" : ""}`}
                            name="email"
                            value={personObj.person.email}
                            onChange={(e) =>
                              handlePersonChange(index, "email", e.target.value)
                            }
                          />
                          {personErrors.email && <div className="invalid-feedback d-block">{personErrors.email}</div>}
                        </div>
                      </div>

                      {/* Mobile No */}
                      <div className="col-md-6 col-sm-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("oquic.personDetails.formLabels.mobileNo")}
                          </label>
                          <input
                            type="text"
                            className={`form-control element-style ${personErrors.mobile_no ? "is-invalid" : ""}`}
                            name="mobile_no"
                            value={personObj.person.mobile_no}
                            onChange={(e) =>
                              handlePersonChange(
                                index,
                                "mobile_no",
                                e.target.value
                              )
                            }
                          />
                          {personErrors.mobile_no && <div className="invalid-feedback d-block">{personErrors.mobile_no}</div>}
                        </div>
                      </div>

                      {/* Address */}
                      <div className="col-md-6 col-sm-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("oquic.personDetails.formLabels.address")}
                          </label>
                          <input
                            type="text"
                            className={`form-control element-style ${personErrors.address ? "is-invalid" : ""}`}
                            name="address"
                            value={personObj.person.address}
                            onChange={(e) =>
                              handlePersonChange(index, "address", e.target.value)
                            }
                          />
                          {personErrors.address && <div className="invalid-feedback d-block">{personErrors.address}</div>}
                        </div>
                      </div>

                      {/* City */}
                      <div className="col-md-6 col-sm-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("oquic.personDetails.formLabels.city")}
                          </label>
                          <input
                            type="text"
                            className={`form-control element-style ${personErrors.city ? "is-invalid" : ""}`}
                            name="city"
                            value={personObj.person.city}
                            onChange={(e) =>
                              handlePersonChange(index, "city", e.target.value)
                            }
                          />
                          {personErrors.city && <div className="invalid-feedback d-block">{personErrors.city}</div>}
                        </div>
                      </div>

                      {/* PO Box */}
                      <div className="col-md-6 col-sm-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("oquic.personDetails.formLabels.poBox")}
                          </label>
                          <input
                            type="text"
                            className={`form-control element-style ${personErrors.poBox ? "is-invalid" : ""}`}
                            name="poBox"
                            value={personObj.person.poBox}
                            onChange={(e) =>
                              handlePersonChange(index, "poBox", e.target.value)
                            }
                          />
                          {personErrors.poBox && <div className="invalid-feedback d-block">{personErrors.poBox}</div>}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {people.length > 0 && (
            <div className="text-end">
              <Button type="submit" className="h-btn">
                {t("oquic.personDetails.buttons.submit")}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default OquicPersonDetailsForm;